import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow, prism } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { MarkdownRendererProps } from '../../types/components';
import './MarkdownRenderer.css';

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  className = '',
  enableSyntaxHighlight = true,
  theme = 'light'
}) => {
  const syntaxTheme = theme === 'dark' ? tomorrow : prism;

  const customComponents = {
    // 代码块渲染
    code({ node, inline, className, children, ...props }: any) {
      const match = /language-(\w+)/.exec(className || '');
      
      if (!inline && match && enableSyntaxHighlight) {
        return (
          <SyntaxHighlighter
            style={syntaxTheme}
            language={match[1]}
            PreTag="div"
            className="rounded-lg my-4"
            {...props}
          >
            {String(children).replace(/\n$/, '')}
          </SyntaxHighlighter>
        );
      }
      
      return (
        <code 
          className={`${className} bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono`} 
          {...props}
        >
          {children}
        </code>
      );
    },

    // 表格渲染
    table: ({ children }: any) => (
      <div className="overflow-x-auto my-6">
        <table className="min-w-full border-collapse border border-gray-300 bg-white rounded-lg shadow-sm">
          {children}
        </table>
      </div>
    ),

    thead: ({ children }: any) => (
      <thead className="bg-gray-50">
        {children}
      </thead>
    ),

    th: ({ children }: any) => (
      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-900 text-sm">
        {children}
      </th>
    ),

    td: ({ children }: any) => (
      <td className="border border-gray-300 px-4 py-3 text-gray-700 text-sm">
        {children}
      </td>
    ),

    // 引用块渲染
    blockquote: ({ children }: any) => (
      <blockquote className="border-l-4 border-blue-500 pl-6 py-2 my-6 bg-blue-50 italic text-gray-700 rounded-r-lg">
        {children}
      </blockquote>
    ),

    // 标题渲染
    h1: ({ children }: any) => (
      <h1 className="text-3xl font-bold mt-8 mb-4 text-gray-900 border-b border-gray-200 pb-2">
        {children}
      </h1>
    ),

    h2: ({ children }: any) => (
      <h2 className="text-2xl font-semibold mt-7 mb-4 text-gray-800 border-b border-gray-100 pb-2">
        {children}
      </h2>
    ),

    h3: ({ children }: any) => (
      <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-800">
        {children}
      </h3>
    ),

    h4: ({ children }: any) => (
      <h4 className="text-lg font-medium mt-5 mb-3 text-gray-700">
        {children}
      </h4>
    ),

    h5: ({ children }: any) => (
      <h5 className="text-base font-medium mt-4 mb-2 text-gray-700">
        {children}
      </h5>
    ),

    h6: ({ children }: any) => (
      <h6 className="text-sm font-medium mt-4 mb-2 text-gray-600 uppercase tracking-wide">
        {children}
      </h6>
    ),

    // 段落渲染
    p: ({ children }: any) => (
      <p className="mb-4 leading-relaxed text-gray-700 text-base">
        {children}
      </p>
    ),

    // 列表渲染
    ul: ({ children }: any) => (
      <ul className="list-disc list-inside mb-4 text-gray-700 space-y-1 ml-4">
        {children}
      </ul>
    ),

    ol: ({ children }: any) => (
      <ol className="list-decimal list-inside mb-4 text-gray-700 space-y-1 ml-4">
        {children}
      </ol>
    ),

    li: ({ children }: any) => (
      <li className="mb-1 leading-relaxed">
        {children}
      </li>
    ),

    // 链接渲染
    a: ({ children, href }: any) => (
      <a 
        href={href} 
        className="text-blue-600 hover:text-blue-800 underline decoration-blue-300 hover:decoration-blue-500 transition-colors"
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    ),

    // 强调渲染
    strong: ({ children }: any) => (
      <strong className="font-semibold text-gray-900">
        {children}
      </strong>
    ),

    em: ({ children }: any) => (
      <em className="italic text-gray-800">
        {children}
      </em>
    ),

    // 分隔线渲染
    hr: () => (
      <hr className="my-8 border-t border-gray-300" />
    ),

    // 删除线渲染
    del: ({ children }: any) => (
      <del className="line-through text-gray-500">
        {children}
      </del>
    ),

    // 任务列表渲染
    input: ({ type, checked, ...props }: any) => {
      if (type === 'checkbox') {
        return (
          <input
            type="checkbox"
            checked={checked}
            readOnly
            className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            {...props}
          />
        );
      }
      return <input type={type} {...props} />;
    },
  };

  return (
    <div className={`markdown-content prose prose-gray max-w-none ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        components={customComponents}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer;